import type { BridgeMessage, BridgeMessageWithoutNonce } from '@healthhub/message-channel';
import { clearAllUserSession } from '@healthhub/react-ui';
import type { RefObject } from 'react';
import { useCallback } from 'react';

import { persistor } from '@shared/stores';
import type { ChatbotElement } from '@shared/types/chatbot';
import { MESSAGE_ACTION } from '@shared/utils/bridge';
import { clearCookies } from '@shared/utils/cookie';

interface CreateMessageChannelCallbackParams {
  consumeBridgeMessage: (id: string) => void;
  sendMessageToNative: (params?: BridgeMessageWithoutNonce) => void;
  subscribeMessages: (listener: (messages: BridgeMessage[]) => void) => (() => void) | void;
}

interface CreateOnLoadScriptCallbackParams extends CreateMessageChannelCallbackParams {
  chatbotRef: RefObject<ChatbotElement | null>;
  isAuthenticated?: boolean;
  logoutRedirectUri?: string;
  oidcClient?: OidcClientAdapter;
}

type MessageChannelCallback = NonNullable<ChatbotElement['messageChannelCallback']>;

interface OidcClientAdapter {
  logoutAsync: (redirectUri?: string) => Promise<unknown>;
  tokens: unknown;
}

export const createMessageChannelCallback =
  ({
    consumeBridgeMessage,
    sendMessageToNative,
    subscribeMessages,
  }: CreateMessageChannelCallbackParams): MessageChannelCallback =>
  () => ({
    consumeBridgeMessage,
    messageActions: () => MESSAGE_ACTION,
    subscribeMessages,
    sendMessageToNative: ({ action, data }) => {
      const actionHandler = (
        MESSAGE_ACTION as Partial<Record<string, (payload?: unknown) => BridgeMessageWithoutNonce>>
      )[action];

      if (typeof actionHandler !== 'function') {
        console.warn(`[Chatbot Bridge] Unknown message action: ${String(action)}`);
        return;
      }

      sendMessageToNative(actionHandler(data));
    },
  });

export const onLoadScriptCallback = ({
  chatbotRef,
  consumeBridgeMessage,
  sendMessageToNative,
  subscribeMessages,
  isAuthenticated = false,
  logoutRedirectUri,
  oidcClient,
}: CreateOnLoadScriptCallbackParams) => {
  if (chatbotRef.current) {
    chatbotRef.current.messageChannelCallback = createMessageChannelCallback({
      consumeBridgeMessage,
      sendMessageToNative,
      subscribeMessages,
    });
  }

  if (chatbotRef.current && isAuthenticated && oidcClient) {
    chatbotRef.current.oidcCallback = () => ({
      isAuthenticated,
      logoutAsync: async () => {
        clearAllUserSession({ persistor, clearCookies });
        await oidcClient.logoutAsync(logoutRedirectUri);
      },
      tokens: oidcClient.tokens,
    });
  }
};

export const useOnLoadScriptCallback = ({
  chatbotRef,
  consumeBridgeMessage,
  isAuthenticated = false,
  logoutRedirectUri,
  oidcClient,
  sendMessageToNative,
  subscribeMessages,
}: CreateOnLoadScriptCallbackParams) =>
  useCallback(() => {
    onLoadScriptCallback({
      chatbotRef,
      consumeBridgeMessage,
      isAuthenticated,
      logoutRedirectUri,
      oidcClient,
      sendMessageToNative,
      subscribeMessages,
    });
  }, [
    chatbotRef,
    consumeBridgeMessage,
    isAuthenticated,
    logoutRedirectUri,
    oidcClient,
    sendMessageToNative,
    subscribeMessages,
  ]);
